import type { SimulationResult } from "./simulationEngine";

export interface MarketSimulationScenario {
  name: string;
  description: string;
  steps: Array<{
    kind:
      | "oracle_update"
      | "order_submit"
      | "batch_match"
      | "queue_consume"
      | "batch_settlement"
      | "risk_check"
      | "liquidation";
    payload: Record<string, string | number | boolean>;
  }>;
}

export interface FeeEnvelope {
  minFeeMicros: number;
  expectedFeeMicros: number;
  worstCaseFeeMicros: number;
  dominantCostDrivers: string[];
}

export interface IntentReceipt {
  intentKind: string;
  status: "accepted" | "rejected" | "matched" | "partially_matched" | "liquidated";
  reasonCode: number;
  feePaidMicros: number;
  oracleCheck: "accepted" | "stale_rejected" | "deviation_rejected" | "circuit_breaker_rejected" | "not_applicable";
  riskCheck: "passed" | "failed" | "not_applicable";
  batchSummary: string;
  builderCode?: number;
  referralCode?: number;
}

export interface ConstraintTrace {
  category: "oracle" | "risk" | "batch" | "policy";
  check: string;
  observed: string;
  expected: string;
  passed: boolean;
}

export interface RiskSnapshot {
  equity: number;
  collateral: number;
  notional: number;
  collateralRatioBps: number;
  initMarginRatioBps: number;
  maintMarginRatioBps: number;
  liquidationThresholdBps: number;
}

export interface VenuePolicyView {
  tickSize: number;
  lotSize: number;
  postOnlySupported: boolean;
  reduceOnlySupported: boolean;
  marginMode: "isolated" | "cross";
  oracleGuard: {
    maxOracleAgeMs: number;
    maxDeviationBps: number;
    breakerTripBps: number;
  };
}

export interface MarketSimulationStepView {
  index: number;
  kind: string;
  status: "ok" | "rejected";
  message: string;
  reasonCode: number;
  feeMicros: number;
  feeEnvelope: FeeEnvelope;
  receipt: IntentReceipt;
  constraintTrace: ConstraintTrace[];
  riskSnapshot?: RiskSnapshot;
  eventPreview: string[];
}

export interface MarketSimulationReport {
  scenario: string;
  deterministicHash: string;
  totalFeeMicros: number;
  worstCaseFeeMicros: number;
  venuePolicy: VenuePolicyView;
  steps: MarketSimulationStepView[];
  rejects: string[];
}

function feeForKind(kind: string): number {
  switch (kind) {
    case "oracle_update":
      return 10000;
    case "order_submit":
      return 12000;
    case "batch_match":
      return 20000;
    case "queue_consume":
      return 6000;
    case "batch_settlement":
      return 14000;
    case "risk_check":
      return 8000;
    case "liquidation":
      return 200000;
    default:
      return 1000;
  }
}

function feeEnvelopeForStep(kind: string, feeMicros: number): FeeEnvelope {
  const driversByKind: Record<string, string[]> = {
    oracle_update: ["oracle validation", "typed event emission"],
    order_submit: ["sorted orderbook mutation", "queue append", "attribution tagging"],
    batch_match: ["bounded traversal", "match receipts"],
    queue_consume: ["fifo dequeue", "queue observability"],
    batch_settlement: ["atomic batch close", "settlement summaries"],
    risk_check: ["margin checks", "risk snapshot emission"],
    liquidation: ["liquidation eligibility", "liquidation event path"],
  };
  return {
    minFeeMicros: feeMicros,
    expectedFeeMicros: feeMicros,
    worstCaseFeeMicros: feeMicros,
    dominantCostDrivers: driversByKind[kind] ?? ["misc deterministic op"],
  };
}

function simpleDeterministicHash(input: string): string {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return `h${(h >>> 0).toString(16)}`;
}

export function runMarketSimulationScenario(
  scenario: MarketSimulationScenario,
  baseResult?: SimulationResult,
): MarketSimulationReport {
  const venuePolicy: VenuePolicyView = {
    tickSize: 1,
    lotSize: 1,
    postOnlySupported: false,
    reduceOnlySupported: false,
    marginMode: "isolated",
    oracleGuard: {
      maxOracleAgeMs: 60000,
      maxDeviationBps: 400,
      breakerTripBps: 1000,
    },
  };
  const steps: MarketSimulationStepView[] = [];
  const rejects: string[] = [];
  let total = 0;
  let worst = 0;

  for (let i = 0; i < scenario.steps.length; i++) {
    const step = scenario.steps[i];
    const fee = feeForKind(step.kind);
    total += fee;
    if (fee > worst) {
      worst = fee;
    }

    let status: "ok" | "rejected" = "ok";
    let message = `${step.kind} applied`;
    let reasonCode = 0;
    const events: string[] = [];
    const constraintTrace: ConstraintTrace[] = [];
    let riskSnapshot: RiskSnapshot | undefined;
    const feeEnvelope = feeEnvelopeForStep(step.kind, fee);

    if (step.kind === "oracle_update") {
      const maxOracleAge = Number(step.payload.max_oracle_age_ms ?? venuePolicy.oracleGuard.maxOracleAgeMs);
      const stale = Number(step.payload.oracle_age_ms ?? 0) > maxOracleAge;
      const deviation = Number(step.payload.deviation_bps ?? 0);
      const maxDeviation = Number(step.payload.max_deviation_bps ?? venuePolicy.oracleGuard.maxDeviationBps);
      const breakerTripBps = Number(step.payload.breaker_trip_bps ?? venuePolicy.oracleGuard.breakerTripBps);
      const breakerActive = Boolean(step.payload.breaker_active ?? false);

      constraintTrace.push({
        category: "oracle",
        check: "oracle_age_ms <= max_oracle_age_ms",
        observed: String(step.payload.oracle_age_ms ?? 0),
        expected: `<= ${maxOracleAge}`,
        passed: !stale,
      });

      constraintTrace.push({
        category: "oracle",
        check: "deviation_bps <= max_deviation_bps",
        observed: String(deviation),
        expected: `<= ${maxDeviation}`,
        passed: deviation <= maxDeviation,
      });

      if (stale) {
        status = "rejected";
        message = "oracle stale rejection";
        reasonCode = 4001;
        rejects.push(message);
        events.push("OracleReadRejected");
      } else if (breakerActive) {
        status = "rejected";
        message = "oracle circuit breaker rejection";
        reasonCode = 4004;
        rejects.push(message);
        events.push("OracleReadRejected");
      } else if (deviation > breakerTripBps) {
        status = "rejected";
        message = "oracle deviation exceeded breaker threshold";
        reasonCode = 4003;
        rejects.push(message);
        events.push("OracleReadRejected");
      } else if (deviation > maxDeviation) {
        status = "rejected";
        message = "oracle deviation rejection";
        reasonCode = 4002;
        rejects.push(message);
        events.push("OracleReadRejected");
      } else {
        reasonCode = 0;
        events.push("OracleReadValidated");
      }
    }

    if (step.kind === "order_submit") {
      const builderCode = Number(step.payload.builder_code ?? 0);
      const maxFeeMicros = Number(step.payload.max_fee_micros ?? feeEnvelope.expectedFeeMicros);
      constraintTrace.push({
        category: "policy",
        check: "builder_code in [100000, 999999]",
        observed: String(builderCode),
        expected: "100000..999999",
        passed: builderCode >= 100000 && builderCode <= 999999,
      });
      constraintTrace.push({
        category: "policy",
        check: "max_fee_micros >= expected_fee",
        observed: String(maxFeeMicros),
        expected: `>= ${feeEnvelope.expectedFeeMicros}`,
        passed: maxFeeMicros >= feeEnvelope.expectedFeeMicros,
      });

      if (builderCode < 100000 || builderCode > 999999) {
        status = "rejected";
        message = "invalid builder code";
        reasonCode = 5201;
        rejects.push(message);
      } else if (maxFeeMicros < feeEnvelope.expectedFeeMicros) {
        status = "rejected";
        message = "deterministic fee envelope exceeded max fee";
        reasonCode = 5202;
        rejects.push(message);
      } else {
        events.push("FeePreviewCalculated", "BuilderAttributed", "ReferralAttributed", "OrderPlaced", "QueuePositionUpdated");
      }
    }

    if (step.kind === "batch_match") {
      const matched = Number(step.payload.matched_levels ?? 1) > 1;
      events.push(matched ? "TradeMatched" : "TradeMatched", matched ? "QueueConsumed" : "QueueConsumed");
      if (matched) {
        message = "multi-level bounded match applied";
      } else {
        message = "single-level bounded match applied";
      }
    }

    if (step.kind === "queue_consume") {
      const processed = Number(step.payload.processed ?? 0);
      const remaining = Number(step.payload.remaining ?? 0);
      constraintTrace.push({
        category: "batch",
        check: "processed <= queue_len_before",
        observed: String(processed),
        expected: ">= 0 and bounded",
        passed: processed >= 0,
      });
      events.push("QueueConsumed");
      message = `queue consumed at one price level: processed=${processed}, remaining=${remaining}`;
    }

    if (step.kind === "batch_settlement") {
      events.push("BatchSettled");
      message = "bounded settlement batch finalized";
    }

    if (step.kind === "risk_check") {
      const collateral = Number(step.payload.collateral ?? 1000000);
      const notional = Number(step.payload.notional ?? 100000);
      const initMarginBps = Number(step.payload.init_margin_bps ?? 1000);
      const maintMarginBps = Number(step.payload.maint_margin_bps ?? 500);
      const requiredInit = Math.floor((notional * initMarginBps) / 10000);
      const requiredMaint = Math.floor((notional * maintMarginBps) / 10000);
      const passByRule = collateral >= requiredInit;
      const ok = Boolean(step.payload.passed ?? passByRule);
      const collateralRatioBps = notional > 0 ? Math.floor((collateral * 10000) / notional) : 1000000;

      riskSnapshot = {
        equity: collateral - notional,
        collateral,
        notional,
        collateralRatioBps,
        initMarginRatioBps: initMarginBps,
        maintMarginRatioBps: maintMarginBps,
        liquidationThresholdBps: maintMarginBps,
      };

      constraintTrace.push({
        category: "risk",
        check: "collateral >= required_init_margin",
        observed: String(collateral),
        expected: `>= ${requiredInit}`,
        passed: collateral >= requiredInit,
      });
      constraintTrace.push({
        category: "risk",
        check: "collateral >= required_maint_margin",
        observed: String(collateral),
        expected: `>= ${requiredMaint}`,
        passed: collateral >= requiredMaint,
      });

      events.push("RiskSnapshotEvaluated");
      events.push(ok ? "MarginCheckPassed" : "MarginCheckFailed");
      if (!ok) {
        status = "rejected";
        message = "risk guard rejection";
        reasonCode = 5003;
        rejects.push(message);
      }
    }

    if (step.kind === "liquidation") {
      const trigger = Boolean(step.payload.triggered ?? false);
      constraintTrace.push({
        category: "risk",
        check: "liquidation_eligibility == true",
        observed: String(trigger),
        expected: "true",
        passed: trigger,
      });
      if (trigger) {
        events.push("LiquidationTriggered", "IntentReceiptGenerated");
        message = "liquidation triggered under maintenance threshold";
      } else {
        status = "rejected";
        message = "liquidation not eligible";
        reasonCode = 5005;
        rejects.push(message);
      }
    }

    const receipt: IntentReceipt = {
      intentKind: step.kind,
      status:
        step.kind === "liquidation" && status === "ok"
          ? "liquidated"
          : step.kind === "batch_match" && status === "ok"
            ? "matched"
            : status === "ok"
              ? "accepted"
              : "rejected",
      reasonCode,
      feePaidMicros: feeEnvelope.expectedFeeMicros,
      oracleCheck:
        step.kind === "oracle_update"
          ? status === "ok"
            ? "accepted"
            : reasonCode === 4001
              ? "stale_rejected"
              : reasonCode === 4004
                ? "circuit_breaker_rejected"
                : "deviation_rejected"
          : "not_applicable",
      riskCheck:
        step.kind === "risk_check" || step.kind === "liquidation"
          ? status === "ok"
            ? "passed"
            : "failed"
          : "not_applicable",
      batchSummary:
        step.kind === "batch_match"
          ? `matched_levels=${String(step.payload.matched_levels ?? 1)}`
          : step.kind === "batch_settlement"
            ? `settled_ops=${String(step.payload.settled_ops ?? 0)}`
            : "none",
      builderCode: Number(step.payload.builder_code ?? 0) || undefined,
      referralCode: Number(step.payload.builder_code ?? 0) || undefined,
    };

    if (status === "rejected" && constraintTrace.length === 0) {
      constraintTrace.push({
        category: "policy",
        check: "deterministic rejection path",
        observed: "rejected",
        expected: "accepted",
        passed: false,
      });
    }

    steps.push({
      index: i,
      kind: step.kind,
      status,
      message,
      reasonCode,
      feeMicros: fee,
      feeEnvelope,
      receipt,
      constraintTrace,
      riskSnapshot,
      eventPreview: events,
    });
  }

  const hashSource = JSON.stringify({
    scenario,
    base: baseResult?.steps?.map((s) => `${s.nodeType}:${s.status}`) ?? [],
    venuePolicy,
    steps,
  });

  return {
    scenario: scenario.name,
    deterministicHash: simpleDeterministicHash(hashSource),
    totalFeeMicros: total,
    worstCaseFeeMicros: worst,
    venuePolicy,
    steps,
    rejects,
  };
}

export const PREBUILT_MARKET_SCENARIOS: MarketSimulationScenario[] = [
  {
    name: "OrderPlacementAndMatch",
    description: "Order placement with oracle acceptance, attribution, risk pass, and bounded match.",
    steps: [
      {
        kind: "oracle_update",
        payload: {
          oracle_age_ms: 12000,
          max_oracle_age_ms: 60000,
          deviation_bps: 180,
          max_deviation_bps: 400,
        },
      },
      {
        kind: "order_submit",
        payload: {
          side: "bid",
          price: 100000,
          qty: 6,
          builder_code: 123456,
          max_fee_micros: 12000,
        },
      },
      {
        kind: "risk_check",
        payload: {
          collateral: 100000,
          notional: 40000,
          init_margin_bps: 1000,
          maint_margin_bps: 500,
        },
      },
      {
        kind: "batch_match",
        payload: {
          max_ops: 16,
          matched_levels: 1,
        },
      },
    ],
  },
  {
    name: "QueueConsumptionSingleLevel",
    description: "Queue consumption at a single price level with deterministic queue observability.",
    steps: [
      {
        kind: "queue_consume",
        payload: {
          processed: 3,
          remaining: 7,
        },
      },
    ],
  },
  {
    name: "MultiLevelBoundedMatch",
    description: "Bounded matching across multiple levels with deterministic batch outputs.",
    steps: [
      {
        kind: "batch_match",
        payload: {
          max_ops: 24,
          matched_levels: 3,
        },
      },
      {
        kind: "batch_settlement",
        payload: {
          settled_ops: 24,
        },
      },
    ],
  },
  {
    name: "StaleOracleRejection",
    description: "Deterministic stale oracle rejection path.",
    steps: [
      {
        kind: "oracle_update",
        payload: {
          oracle_age_ms: 90000,
          max_oracle_age_ms: 60000,
          deviation_bps: 100,
          max_deviation_bps: 400,
        },
      },
    ],
  },
  {
    name: "MaintenanceThresholdBreach",
    description: "Risk check fails exactly at maintenance threshold breach.",
    steps: [
      {
        kind: "risk_check",
        payload: {
          collateral: 4000,
          notional: 100000,
          init_margin_bps: 1000,
          maint_margin_bps: 500,
          passed: false,
        },
      },
    ],
  },
  {
    name: "LiquidationTrigger",
    description: "Liquidation eligibility and trigger visibility.",
    steps: [
      {
        kind: "liquidation",
        payload: {
          triggered: true,
        },
      },
    ],
  },
  {
    name: "BatchSettlementVisibility",
    description: "Explicit settlement and queue-consumption event preview for indexer parity.",
    steps: [
      {
        kind: "queue_consume",
        payload: {
          processed: 10,
          remaining: 5,
        },
      },
      {
        kind: "batch_settlement",
        payload: {
          settled_ops: 10,
        },
      },
    ],
  },
  {
    name: "BuilderReferralAttribution",
    description: "Attribution visibility with deterministic fee envelope and receipts.",
    steps: [
      {
        kind: "order_submit",
        payload: {
          side: "ask",
          price: 100200,
          qty: 4,
          builder_code: 654321,
          max_fee_micros: 12000,
        },
      },
    ],
  },
  {
    name: "FeeBudgetComparison",
    description: "Compare deterministic fee budget outcomes across bounded call shapes.",
    steps: [
      {
        kind: "order_submit",
        payload: {
          side: "bid",
          price: 100100,
          qty: 2,
          builder_code: 123456,
          max_fee_micros: 8000,
        },
      },
      {
        kind: "order_submit",
        payload: {
          side: "bid",
          price: 100110,
          qty: 2,
          builder_code: 123456,
          max_fee_micros: 12000,
        },
      },
    ],
  },
];
